import { useMemo } from "react";
import { FiUsers, FiFileText, FiMapPin } from "react-icons/fi";
import { useGetMunicipalitiesQuery } from "../../store/services/municipalitiesServices";

const GameStatistics = ({ data }) => {
    const { data: municipalitiesData } = useGetMunicipalitiesQuery();

    const isPotentials = !!data?.potentials_by_tier;
    const tierRows = data?.winners_by_tier || data?.potentials_by_tier || [];
    const munRows = data?.winners_by_municipality || data?.potentials_by_municipality || [];

    const labels = isPotentials
        ? {
              countWord: "Pjesëmarrës",
              tierTitle: "Pjesëmarrësit sipas kushteve",
              munTitle: "Pjesëmarrësit sipas komunave",
              emptyTier: "Nuk kanë plotësuar asnjë kusht",
              subtitle: "pjesëmarrësit",
          }
        : {
              countWord: "Fitues",
              tierTitle: "Fituesit sipas kushteve",
              munTitle: "Fituesit sipas komunave",
              emptyTier: "Nuk kanë arritur asnjë nivel",
              subtitle: "fituesit",
          };

    const totals = useMemo(() => {
        const participants = tierRows.reduce((s, i) => s + (Number(i.total_winners) || 0), 0);
        const receipts = tierRows.reduce((s, i) => s + (parseInt(i.total_receipts) || 0), 0);
        const communes = munRows.filter((i) => i.municipality_no !== null).length;
        return { participants, receipts, communes };
    }, [tierRows, munRows]);

    const tierMax = Math.max(1, ...tierRows.map((i) => Number(i.total_winners) || 0));
    const munMax = Math.max(1, ...munRows.map((i) => Number(i.total_winners) || 0));

    const munName = (no) =>
        municipalitiesData?.data?.find((m) => m.municipality_no === no)?.municipality_name;

    const formatNum = (n) =>
        n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString();

    return (
        <div className="max-w-6xl mx-auto flex flex-col gap-6">
            <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                    Raporti i statistikave
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                    Të dhënat e fundit për {labels.subtitle} dhe kuponat.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard label={labels.countWord} value={totals.participants} icon={<FiUsers size={16} />} />
                <StatCard label="Kupona" value={totals.receipts} icon={<FiFileText size={16} />} />
                <StatCard label="Komuna" value={totals.communes} icon={<FiMapPin size={16} />} />
            </div>

            <Section title={labels.tierTitle}>
                <BreakdownTable
                    headLabels={["Kushti", labels.countWord, "Kupona"]}
                    rows={tierRows}
                    max={tierMax}
                    renderLabel={(item) =>
                        item.tier_level ? (
                            `Kushti ${item.tier_level}`
                        ) : (
                            <span className="italic text-gray-400">{labels.emptyTier}</span>
                        )
                    }
                    renderValues={(item) => [item.total_winners, item.total_receipts]}
                    formatNum={formatNum}
                />
            </Section>

            <Section title={labels.munTitle}>
                <BreakdownTable
                    headLabels={["Komuna", labels.countWord]}
                    rows={munRows}
                    max={munMax}
                    renderLabel={(item) =>
                        munName(item.municipality_no) || (
                            <span className="italic text-gray-400">Pa komunë</span>
                        )
                    }
                    renderValues={(item) => [item.total_winners]}
                    formatNum={formatNum}
                />
            </Section>
        </div>
    );
};

const StatCard = ({ label, value, icon }) => (
    <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {label}
            </span>
            <span className="text-gray-400">{icon}</span>
        </div>
        <div className="mt-2 text-3xl font-bold text-gray-900 tabular-nums">
            {(value ?? 0).toLocaleString()}
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <div>
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2 px-1">
            {title}
        </h2>
        {children}
    </div>
);

const BreakdownTable = ({ headLabels, rows, max, renderLabel, renderValues, formatNum }) => {
    if (!rows?.length) {
        return (
            <div className="rounded-2xl border border-gray-200/80 bg-white py-12 text-center text-sm text-gray-500 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                Nuk ka të dhëna për të shfaqur.
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-100">
                        {headLabels.map((label, i) => (
                            <th
                                key={i}
                                className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${
                                    i === 0 ? "text-left" : "text-right"
                                }`}
                            >
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((item, i) => {
                        const values = renderValues(item);
                        const primary = Number(values[0]) || 0;
                        const share = max > 0 ? primary / max : 0;
                        return (
                            <tr key={i} className="hover:bg-gray-50/60 transition">
                                <td className="px-5 py-3 align-top w-[55%]">
                                    <div className="text-gray-800">{renderLabel(item)}</div>
                                    <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-secondary rounded-full transition-all"
                                            style={{ width: `${Math.max(share * 100, primary > 0 ? 4 : 0)}%` }}
                                        />
                                    </div>
                                </td>
                                {values.map((v, j) => (
                                    <td
                                        key={j}
                                        className={`px-5 py-3 text-right tabular-nums align-top whitespace-nowrap ${
                                            j === 0
                                                ? "font-semibold text-gray-900"
                                                : "text-gray-600"
                                        }`}
                                    >
                                        {formatNum(v)}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default GameStatistics;
